import type { ChangeEvent } from 'react';
import type { InputProps } from '../../../components/Input/Input';
import type { UseAppForm } from '../../form';

import { useMemo } from 'react';
import moment from 'moment';
import slug from 'slug';

import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import Input from '../../../components/Input/Input';

import { RefreshIcon } from '../../../images/shapes';

import useYjsField from '../../collaboration/useYjsField';
import { useFieldContext, useFormContext } from '../../form';
import { useFieldErrors } from '../../useFieldErrors';

const InputField = ({
  type,
  onFocus,
  onBlur,
  disabled,
  slugRules,
  handleSlugCreationOnBlur,
  ...props
}: InputProps) => {
  const field = useFieldContext<string | number>();
  const form = useFormContext() as unknown as UseAppForm;
  const errors = useFieldErrors(field.name);

  const yjsField = useYjsField(
    typeof field.state.value === 'string' ? 'string' : 'raw',
  );

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    let value: string | number = e.target.value;
    if (type === 'number' && !isNaN(Number(value))) {
      value = Number(value);
    }
    field.handleChange(value);
    yjsField.update(value);
  };

  const value = useMemo(() => {
    let value = field.state.value;
    if (type && ['date', 'datetime-local'].includes(type) && value) {
      value = moment(value).format(
        type === 'date' ? 'yyyy-MM-DD' : 'yyyy-MM-DDTHH:mm',
      );
    }
    return value;
  }, [field.state.value, type]);

  const onFieldFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    onFocus?.(e);
    yjsField.onFocus();

    if (slugRules?.active && !value) {
      const sourceValue = form.getFieldValue(slugRules.source) as string;

      const slugValue = slug(sourceValue);
      form.setFieldValue(slugRules.target, slugValue);
      yjsField.update(slugValue);
    }
  };

  const onFieldBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlur?.(e);
    if (handleSlugCreationOnBlur) {
      handleSlugCreationOnBlur();
    }
    field.handleBlur();
    yjsField.onBlur();
  };

  const onSlugRefresh = () => {
    if (slugRules?.active) {
      const sourceValue = form.getFieldValue(slugRules.source) as string;
      const slugValue = slug(sourceValue);

      form.setFieldValue(slugRules.target, slugValue);
      yjsField.update(slugValue);
    }
  };

  const InputComponent = (
    <Input
      {...props}
      type={type}
      name={field.name}
      value={value}
      onChange={onChange}
      error={errors}
      onFocus={onFieldFocus}
      onBlur={onFieldBlur}
      disabled={disabled || yjsField.isBeingEdited}
      editors={yjsField.fieldEditors}
    />
  );

  if (slugRules?.active) {
    return (
      <div className="relative outli">
        <Button
          onClick={onSlugRefresh}
          buttonColor="transparent"
          additionalClasses="absolute top-[45%] -left-8 p-0! hover:bg-transparent
           hover:outline-hidden hover:border-transparent"
          {...getTestProps(props.testId, 'slug-refresh', 'testId')}
        >
          <RefreshIcon className="w-4 h-4 text-blue" />
        </Button>
        {InputComponent}
      </div>
    );
  }

  return <>{InputComponent}</>;
};

export default InputField;
